/**
 * smartSearch.js
 *
 * A multi-signal relevance engine for college name lookup.
 *
 * Scoring tiers (higher = better):
 *   1000+ : Exact official name match (case-insensitive)
 *    900+ : Exact alias / abbreviation match
 *    800+ : Official name starts with query (prefix match)
 *    700+ : Alias starts with query
 *    600+ : Official name contains query as a whole word
 *    500+ : Alias contains query as a whole word
 *    300+ : Fuse.js fuzzy match on official name   (score mapped 0-299)
 *    200+ : Fuse.js fuzzy match on alias           (score mapped 0-199)
 *      0  : No match — excluded from results
 *
 * Each tier score is then boosted by a popularity signal
 * (student size, capped and normalised to [0, 50]) so well-known
 * schools surface first when two results tie on textual similarity.
 */

import Fuse from 'fuse.js';

// ---------------------------------------------------------------------------
// 1.  Text normalisation
// ---------------------------------------------------------------------------

/**
 * Normalise a string for comparison:
 *   - lowercase
 *   - collapse whitespace
 *   - strip most punctuation (keep hyphens between words so "rose-hulman" works)
 */
export function normalise(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[''`]/g, '')          // remove apostrophes
    .replace(/[^\w\s-]/g, ' ')      // replace non-word chars (except hyphen) with space
    .replace(/\s+/g, ' ')           // collapse whitespace
    .trim();
}

// ---------------------------------------------------------------------------
// 2.  Alias / abbreviation dictionary
//     Keys are lowercase normalised query strings.
//     Values are one or more substrings that appear in the official name.
// ---------------------------------------------------------------------------
export const ALIAS_MAP = {
  // Ivy League & elite privates
  'harvard':           ['Harvard University'],
  'yale':              ['Yale University'],
  'princeton':         ['Princeton University'],
  'columbia':          ['Columbia University'],
  'penn':              ['University of Pennsylvania'],
  'upenn':             ['University of Pennsylvania'],
  'u penn':            ['University of Pennsylvania'],
  'dartmouth':         ['Dartmouth College'],
  'brown':             ['Brown University'],
  'cornell':           ['Cornell University'],
  'mit':               ['Massachusetts Institute of Technology'],
  'massachusetts institute': ['Massachusetts Institute of Technology'],
  'stanford':          ['Stanford University'],
  'caltech':           ['California Institute of Technology'],
  'california institute': ['California Institute of Technology'],
  'duke':              ['Duke University'],
  'vanderbilt':        ['Vanderbilt University'],
  'vandy':             ['Vanderbilt University'],
  'notre dame':        ['University of Notre Dame'],
  'nd':                ['University of Notre Dame'],
  'uchicago':          ['University of Chicago'],
  'u chicago':         ['University of Chicago'],
  'northwestern':      ['Northwestern University'],
  'emory':             ['Emory University'],
  'georgetown':        ['Georgetown University'],
  'tufts':             ['Tufts University'],
  'wustl':             ['Washington University in St Louis', 'Washington University in St. Louis'],
  'washu':             ['Washington University in St Louis', 'Washington University in St. Louis'],
  'wash u':            ['Washington University in St Louis', 'Washington University in St. Louis'],
  'wake forest':       ['Wake Forest University'],
  'boston college':     ['Boston College'],
  'bc':                ['Boston College'],
  'bu':                ['Boston University'],
  'boston university':  ['Boston University'],
  'northeastern':      ['Northeastern University'],
  'nyu':               ['New York University'],
  'new york university': ['New York University'],
  'fordham':           ['Fordham University'],

  // Large public flagships
  'michigan':          ['University of Michigan'],
  'umich':             ['University of Michigan'],
  'uchigan':           ['University of Michigan'],
  'virginia':          ['University of Virginia'],
  'uva':               ['University of Virginia'],
  'north carolina':    ['University of North Carolina'],
  'unc':               ['University of North Carolina'],
  'chapel hill':       ['University of North Carolina at Chapel Hill'],
  'florida':           ['University of Florida'],
  'uf':                ['University of Florida'],
  'georgia':           ['University of Georgia'],
  'uga':               ['University of Georgia'],
  'texas':             ['University of Texas'],
  'ut austin':         ['University of Texas at Austin'],
  'ut':                ['University of Texas'],
  'ohio state':        ['Ohio State University'],
  'osu':               ['Ohio State University', 'Oregon State University', 'Oklahoma State University'],
  'penn state':        ['Pennsylvania State University'],
  'psu':               ['Pennsylvania State University'],
  'purdue':            ['Purdue University'],
  'indiana university': ['Indiana University'],
  'iu':                ['Indiana University Bloomington', 'Indiana University'],
  'iowa':              ['University of Iowa'],
  'minnesota':         ['University of Minnesota'],
  'umn':               ['University of Minnesota'],
  'wisconsin':         ['University of Wisconsin'],
  'uw':                ['University of Wisconsin', 'University of Washington'],
  'illinois':          ['University of Illinois'],
  'uiuc':              ['University of Illinois at Urbana-Champaign', 'University of Illinois Urbana-Champaign'],
  'u of i':            ['University of Illinois'],
  'maryland':          ['University of Maryland'],
  'umd':               ['University of Maryland'],
  'rutgers':           ['Rutgers University'],
  'connecticut':       ['University of Connecticut'],
  'uconn':             ['University of Connecticut'],
  'pittsburgh':        ['University of Pittsburgh'],
  'pitt':              ['University of Pittsburgh'],
  'upitt':             ['University of Pittsburgh'],
  'carnegie mellon':   ['Carnegie Mellon University'],
  'cmu':               ['Carnegie Mellon University'],
  'drexel':            ['Drexel University'],
  'temple':            ['Temple University'],

  // UC system
  'uc berkeley':       ['University of California-Berkeley', 'University of California, Berkeley'],
  'berkeley':          ['University of California-Berkeley', 'University of California, Berkeley'],
  'cal':               ['University of California-Berkeley', 'University of California, Berkeley'],
  'ucla':              ['University of California-Los Angeles', 'University of California, Los Angeles'],
  'ucsd':              ['University of California-San Diego', 'University of California, San Diego'],
  'ucsb':              ['University of California-Santa Barbara', 'University of California, Santa Barbara'],
  'uci':               ['University of California-Irvine', 'University of California, Irvine'],
  'uc irvine':         ['University of California-Irvine'],
  'ucd':               ['University of California-Davis', 'University of California, Davis'],
  'uc davis':          ['University of California-Davis'],
  'ucsc':              ['University of California-Santa Cruz'],
  'uc santa cruz':     ['University of California-Santa Cruz'],
  'ucr':               ['University of California-Riverside'],
  'uc riverside':      ['University of California-Riverside'],
  'ucm':               ['University of California-Merced'],

  // SUNY / CUNY
  'suny':              ['State University of New York'],
  'stony brook':       ['Stony Brook University', 'State University of New York at Stony Brook'],
  'binghamton':        ['Binghamton University', 'State University of New York at Binghamton'],
  'buffalo':           ['University at Buffalo'],
  'ub':                ['University at Buffalo'],
  'cuny':              ['City University of New York'],
  'city college':      ['City College of New York'],

  // Historically Black Colleges & Universities
  'howard':            ['Howard University'],
  'morehouse':         ['Morehouse College'],
  'spelman':           ['Spelman College'],
  'hampton':           ['Hampton University'],
  'famu':              ['Florida A&M University', 'Florida Agricultural and Mechanical University'],
  'fisk':              ['Fisk University'],

  // Technical & specialty schools
  'ga tech':           ['Georgia Institute of Technology'],
  'georgia tech':      ['Georgia Institute of Technology'],
  'gatech':            ['Georgia Institute of Technology'],
  'rpi':               ['Rensselaer Polytechnic Institute'],
  'rensselaer':        ['Rensselaer Polytechnic Institute'],
  'virginia tech':     ['Virginia Polytechnic Institute and State University', 'Virginia Tech'],
  'vt':                ['Virginia Polytechnic Institute and State University'],
  'vtech':             ['Virginia Polytechnic Institute and State University'],
  'wpi':               ['Worcester Polytechnic Institute'],
  'worcester':         ['Worcester Polytechnic Institute'],
  'rose hulman':       ['Rose-Hulman Institute of Technology'],
  'rose-hulman':       ['Rose-Hulman Institute of Technology'],
  'harvey mudd':       ['Harvey Mudd College'],
  'mudd':              ['Harvey Mudd College'],

  // Liberal arts colleges
  'amherst':           ['Amherst College'],
  'williams':          ['Williams College'],
  'swarthmore':        ['Swarthmore College'],
  'wellesley':         ['Wellesley College'],
  'smith':             ['Smith College'],
  'middlebury':        ['Middlebury College'],
  'colby':             ['Colby College'],
  'bates':             ['Bates College'],
  'bowdoin':           ['Bowdoin College'],
  'colgate':           ['Colgate University'],
  'hamilton':          ['Hamilton College'],
  'vassar':            ['Vassar College'],
  'haverford':         ['Haverford College'],
  'bryn mawr':         ['Bryn Mawr College'],
  'carleton':          ['Carleton College'],
  'macalester':        ['Macalester College'],
  'grinnell':          ['Grinnell College'],
  'oberlin':           ['Oberlin College'],
  'kenyon':            ['Kenyon College'],
  'denison':           ['Denison University'],
  'dickinson':         ['Dickinson College'],
  'gettysburg':        ['Gettysburg College'],
  'lafayette':         ['Lafayette College'],
  'lehigh':            ['Lehigh University'],
  'bucknell':          ['Bucknell University'],
  'ursinus':           ['Ursinus College'],

  // Regional schools & others
  'uri':               ['University of Rhode Island'],
  'rhode island':      ['University of Rhode Island'],
  'unh':               ['University of New Hampshire'],
  'umass':             ['University of Massachusetts'],
  'umass amherst':     ['University of Massachusetts Amherst'],
  'uvm':               ['University of Vermont'],
  'vermont':           ['University of Vermont'],
  'maine':             ['University of Maine'],
  'umaine':            ['University of Maine'],
  'miami ohio':        ['Miami University'],
  'ohio u':            ['Ohio University'],
  'kent state':        ['Kent State University'],
  'bowling green':     ['Bowling Green State University'],
  'bgsu':              ['Bowling Green State University'],
  'ball state':        ['Ball State University'],
  'depaul':            ['DePaul University'],
  'loyola chicago':    ['Loyola University Chicago'],
  'marquette':         ['Marquette University'],
  'creighton':         ['Creighton University'],
  'gonzaga':           ['Gonzaga University'],
  'villanova':         ['Villanova University'],
  'nova':              ['Villanova University'],
  'seton hall':        ['Seton Hall University'],
  'quinnipiac':        ['Quinnipiac University'],
  'fairfield':         ['Fairfield University'],
  'providence':        ['Providence College'],
  'holy cross':        ['College of the Holy Cross'],
  'santa clara':       ['Santa Clara University'],
  'usc':               ['University of Southern California'],
  'southern california': ['University of Southern California'],
  'pepperdine':        ['Pepperdine University'],
  'lmu':               ['Loyola Marymount University'],
  'sdsu':              ['San Diego State University'],
  'sjsu':              ['San Jose State University'],
  'sfsu':              ['San Francisco State University'],
  'cal poly':          ['California Polytechnic State University'],
  'cal poly slo':      ['California Polytechnic State University-San Luis Obispo'],
  'calpoly':           ['California Polytechnic State University'],
  'csuf':              ['California State University-Fullerton'],
  'csulb':             ['California State University-Long Beach'],
  'csun':              ['California State University-Northridge'],
  'asu':               ['Arizona State University'],
  'arizona state':     ['Arizona State University'],
  'uarizona':          ['University of Arizona'],
  'u of a':            ['University of Arizona'],
  'u arizona':         ['University of Arizona'],
  'unm':               ['University of New Mexico'],
  'utah':              ['University of Utah'],
  'utah state':        ['Utah State University'],
  'byu':               ['Brigham Young University'],
  'brigham young':     ['Brigham Young University'],
  'colorado state':    ['Colorado State University'],
  'csu':               ['Colorado State University', 'Cleveland State University'],
  'cu boulder':        ['University of Colorado Boulder'],
  'colorado':          ['University of Colorado'],
  'wyoming':           ['University of Wyoming'],
  'nevada':            ['University of Nevada'],
  'unlv':              ['University of Nevada-Las Vegas', 'University of Nevada, Las Vegas'],
  'unr':               ['University of Nevada-Reno', 'University of Nevada, Reno'],
  'boise state':       ['Boise State University'],
  'idaho':             ['University of Idaho'],
  'montana':           ['University of Montana'],
  'north dakota':      ['University of North Dakota'],
  'und':               ['University of North Dakota'],
  'south dakota':      ['University of South Dakota'],
  'usd':               ['University of South Dakota'],
  'nebraska':          ['University of Nebraska'],
  'unl':               ['University of Nebraska-Lincoln'],
  'kansas':            ['University of Kansas'],
  'ku':                ['University of Kansas'],
  'k state':           ['Kansas State University'],
  'ksu':               ['Kansas State University', 'Kennesaw State University'],
  'oklahoma':          ['University of Oklahoma'],
  'ou':                ['University of Oklahoma'],
  'arkansas':          ['University of Arkansas'],
  'uark':              ['University of Arkansas'],
  'mississippi':       ['University of Mississippi'],
  'ole miss':          ['University of Mississippi'],
  'msstate':           ['Mississippi State University'],
  'miss state':        ['Mississippi State University'],
  'alabama':           ['University of Alabama'],
  'ua':                ['University of Alabama'],
  'auburn':            ['Auburn University'],
  'lsu':               ['Louisiana State University'],
  'tulane':            ['Tulane University'],
  'tulsa':             ['University of Tulsa'],
  'tennessee':         ['University of Tennessee'],
  'ut knoxville':      ['University of Tennessee at Knoxville', 'University of Tennessee, Knoxville'],
  'kentucky':          ['University of Kentucky'],
  'uk':                ['University of Kentucky'],
  'wku':               ['Western Kentucky University'],
  'eku':               ['Eastern Kentucky University'],
  'louisville':        ['University of Louisville'],
  'uofl':              ['University of Louisville'],
  'west virginia':     ['West Virginia University'],
  'wvu':               ['West Virginia University'],
  'clemson':           ['Clemson University'],
  'carolina':          ['University of South Carolina', 'University of North Carolina'],
  'usc columbia':      ['University of South Carolina'],
  'sc':                ['University of South Carolina'],
  'vcu':               ['Virginia Commonwealth University'],
  'gmu':               ['George Mason University'],
  'jmu':               ['James Madison University'],
  'old dominion':      ['Old Dominion University'],
  'odu':               ['Old Dominion University'],
  'william and mary':  ['College of William and Mary'],
  'w&m':               ['College of William and Mary'],
  'towson':            ['Towson University'],
  'loyola maryland':   ['Loyola University Maryland'],
  'american':          ['American University'],
  'gwu':               ['George Washington University'],
  'george washington': ['George Washington University'],
  'howard dc':         ['Howard University'],

  // Misc common short forms
  'tech':              ['Georgia Institute of Technology', 'Virginia Polytechnic Institute'],
  'state':             ['Pennsylvania State University', 'Ohio State University'],
};

// ---------------------------------------------------------------------------
// 3.  Pre-computed alias lookup structures (built once at module load)
//     This avoids re-normalising and re-iterating on every keystroke.
// ---------------------------------------------------------------------------

// normalised alias → normalised target names
const NORMALISED_ALIAS_ENTRIES = Object.entries(ALIAS_MAP).map(
  ([alias, names]) => [normalise(alias), names.map(n => normalise(n))]
);

// ---------------------------------------------------------------------------
// 4.  Popularity signal
//     Uses student size as a proxy for how well-known the school is.
//     Returns a boost in [0, 50].
// ---------------------------------------------------------------------------
const MAX_SIZE_FOR_BOOST = 60_000;

export function popularityBoost(schoolEntry) {
  const size = Math.max(0, schoolEntry?.['latest.student.size'] ?? 0);
  return Math.min(size / MAX_SIZE_FOR_BOOST, 1) * 50;
}

// ---------------------------------------------------------------------------
// 5.  Alias lookup (optimised — uses pre-normalised entries)
//     Given a normalised query, return every official-name substring that
//     any matching alias maps to.
// ---------------------------------------------------------------------------

export function getAliasTargets(normQuery) {
  const targets = new Set();
  for (const [normAlias, normNames] of NORMALISED_ALIAS_ENTRIES) {
    if (normQuery === normAlias ||
        normQuery.startsWith(normAlias) ||
        normAlias.startsWith(normQuery)) {
      for (const n of normNames) targets.add(n);
    }
  }
  return targets; // Set of normalised target name fragments
}

// ---------------------------------------------------------------------------
// 6.  Word-boundary containment helper (with regex cache)
// ---------------------------------------------------------------------------

const wholeWordCache = new Map();

function containsWholeWord(haystack, needle) {
  let re = wholeWordCache.get(needle);
  if (!re) {
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    re = new RegExp(`(^|\\s)${escaped}(\\s|$)`);
    wholeWordCache.set(needle, re);
  }
  return re.test(haystack);
}

// ---------------------------------------------------------------------------
// 7.  Core scoring function
//     Returns a numeric relevance score for one school entry.
//     Returns 0 if the entry should be excluded entirely.
// ---------------------------------------------------------------------------

export function scoreEntry(normQuery, schoolEntry, aliasTargets) {
  if (!normQuery) return 0;

  // Allow callers to omit aliasTargets (backward compat with tests)
  if (!aliasTargets) aliasTargets = getAliasTargets(normQuery);

  const officialName = schoolEntry['latest.school.name'] || '';
  const normName = normalise(officialName);

  // --- Tier 1: exact official name ---
  if (normName === normQuery) return 1000;

  // --- Tier 2: alias exact match ---
  // aliasTargets is passed in to avoid recomputing per entry
  for (const target of aliasTargets) {
    if (normName === target || normName.includes(target)) return 900;
  }

  // --- Tier 3: official name prefix ---
  if (normName.startsWith(normQuery)) return 800;

  // --- Tier 4: alias prefix → name starts with alias target ---
  for (const target of aliasTargets) {
    if (normName.startsWith(target)) return 700;
  }

  // --- Tier 5: official name contains query as whole word ---
  if (normQuery.length >= 3 && containsWholeWord(normName, normQuery)) return 600;

  // --- Tier 6: alias word-boundary containment ---
  for (const target of aliasTargets) {
    if (target.length >= 3 && containsWholeWord(normName, target)) return 500;
  }

  // No tier match — return 0 (caller will handle fuzzy fallback separately)
  return 0;
}

// ---------------------------------------------------------------------------
// 8.  Fuse.js index cache
//     We build the Fuse index once for a given dataset and reuse it.
//     This avoids the expensive O(n) index-build on every keystroke.
// ---------------------------------------------------------------------------

let cachedFuseDataset = null;   // reference to the results array
let cachedFuse = null;          // Fuse instance

function getFuseInstance(results, fuseThreshold) {
  // Only rebuild if the dataset reference changed (new API fetch)
  if (cachedFuseDataset !== results) {
    cachedFuse = new Fuse(results, {
      keys: [{ name: 'schoolName', getFn: (obj) => obj['latest.school.name'] || '' }],
      includeScore: true,
      threshold: fuseThreshold,
      distance: 200,
      minMatchCharLength: 2,
    });
    cachedFuseDataset = results;
  }
  return cachedFuse;
}

// ---------------------------------------------------------------------------
// 9.  Main search function
//
//     smartSearch(query, results, { maxResults, fuseThreshold })
//
//     `results` is the raw API results array (each entry is a flat object
//     with keys like 'latest.school.name', 'latest.student.size', etc.)
//
//     Returns an array of school entries sorted by relevance (best first),
//     limited to `maxResults`.
// ---------------------------------------------------------------------------

export function smartSearch(query, results, { maxResults = 10, fuseThreshold = 0.45 } = {}) {
  if (!query || !results || results.length === 0) return [];

  const normQuery = normalise(query);
  if (!normQuery) return [];

  // Compute alias targets ONCE per search, not per entry
  const aliasTargets = getAliasTargets(normQuery);

  // ---- Phase 1: tier-based scoring ----
  const scored = [];

  // Track if we already have enough high-confidence matches
  // to skip the expensive fuzzy phase entirely
  let highConfidenceCount = 0;

  for (const entry of results) {
    const tier = scoreEntry(normQuery, entry, aliasTargets);
    if (tier > 0) {
      const boost = popularityBoost(entry);
      scored.push({ entry, score: tier + boost });
      if (tier >= 500) highConfidenceCount++;
    }
  }

  // ---- Phase 2: fuzzy scoring via Fuse.js ----
  // Skip fuzzy entirely if we already have plenty of strong matches,
  // or if the query is too short for meaningful fuzzy matching.
  if (normQuery.length >= 3 && highConfidenceCount < maxResults) {
    const fuse = getFuseInstance(results, fuseThreshold);
    const fuseResults = fuse.search(query, { limit: maxResults * 2 });

    // Build a Set of names we already scored to avoid duplicates
    const alreadyScored = new Set(scored.map(s => s.entry['latest.school.name']));

    for (const { item, score: fuseScore } of fuseResults) {
      const name = item['latest.school.name'] || '';
      if (alreadyScored.has(name)) continue;

      const normName = normalise(name);
      const isAliasMatch = [...aliasTargets].some(t => normName.includes(t));
      const base = isAliasMatch ? 200 : 300;
      const fuzzyScore = base * (1 - fuseScore);
      const boost = popularityBoost(item);
      if (fuzzyScore > 0) {
        scored.push({ entry: item, score: fuzzyScore + boost });
        alreadyScored.add(name);
      }
    }
  }

  // ---- Phase 3: sort by score descending, deduplicate, slice ----
  scored.sort((a, b) => b.score - a.score);

  const seen = new Set();
  const deduped = [];
  for (const { entry } of scored) {
    const name = entry['latest.school.name'];
    if (!seen.has(name)) {
      seen.add(name);
      deduped.push(entry);
      if (deduped.length >= maxResults) break;
    }
  }

  return deduped;
}

// ---------------------------------------------------------------------------
// 10. Autocomplete suggestions helper
//     Returns just the school names (strings) for rendering in a dropdown.
// ---------------------------------------------------------------------------

export function getAutocompleteSuggestions(query, results, maxResults = 10) {
  const matches = smartSearch(query, results, { maxResults });
  return matches.map(entry => entry['latest.school.name']).filter(Boolean);
}
